// SessionContext.js - Chat session management
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import config from '../config';
import { useAuth } from './AuthContext';

const SessionContext = createContext(null);

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};

export const SessionProvider = ({ children }) => {
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [sessions, setSessions] = useState([]);
  // Persist currentSessionId in sessionStorage to survive hot reload
  const [currentSessionId, setCurrentSessionId] = useState(() => {
    const cached = sessionStorage.getItem('currentSessionId');
    return cached || null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch sessions when authenticated
  const fetchSessions = useCallback(async () => {
    if (!isAuthenticated) {
      setSessions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/sessions`, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions || []);
      } else {
        console.error('Failed to fetch sessions');
        setSessions([]);
      }
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, getAuthHeaders]);

  // Fetch sessions on auth change
  useEffect(() => {
    if (isAuthenticated) {
      fetchSessions();
    } else {
      setSessions([]);
      setCurrentSessionId(null);
      sessionStorage.removeItem('currentSessionId');
    }
  }, [isAuthenticated, fetchSessions]);

  // Create a new session (or get existing empty session)
  const createSession = useCallback(async (title = null) => {
    if (!isAuthenticated) return null;

    try {
      const response = await fetch(`${config.API_BASE_URL}/sessions`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title })
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.existing) {
          // An existing empty session was returned - just select it
          // Make sure it's at the top of the list and select it
          setSessions(prev => {
            const filtered = prev.filter(s => s.id !== data.session.id);
            return [data.session, ...filtered];
          });
        } else {
          // A new session was created - add it to the list
          setSessions(prev => [data.session, ...prev]);
        }
        
        setCurrentSessionId(data.session.id);
        sessionStorage.setItem('currentSessionId', data.session.id);
        return data.session;
      }
      return null;
    } catch (err) {
      console.error('Error creating session:', err);
      setError(err.message);
      return null;
    }
  }, [isAuthenticated, getAuthHeaders]);

  // Get session messages
  const getSessionMessages = useCallback(async (sessionId) => {
    if (!isAuthenticated) return [];

    try {
      const response = await fetch(`${config.API_BASE_URL}/sessions/${sessionId}`, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        return data.messages || [];
      } else if (response.status === 404) {
        console.warn(`Session ${sessionId} not found. Clearing selection.`);
        // If the requested session is the current one, clear it
        if (sessionId === currentSessionId) {
          setCurrentSessionId(null);
          sessionStorage.removeItem('currentSessionId');
        }
        return [];
      }
      return [];
    } catch (err) {
      console.error('Error fetching session messages:', err);
      return [];
    }
  }, [isAuthenticated, getAuthHeaders]);

  // Delete a session
  const deleteSession = useCallback(async (sessionId) => {
    if (!isAuthenticated) return false;

    try {
      const response = await fetch(`${config.API_BASE_URL}/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setSessions(prev => prev.filter(s => s.id !== sessionId));
        if (currentSessionId === sessionId) {
          setCurrentSessionId(null);
          sessionStorage.removeItem('currentSessionId');
        }
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error deleting session:', err);
      setError(err.message);
      return false;
    }
  }, [isAuthenticated, getAuthHeaders, currentSessionId]);

  // Update a session's title
  const updateSessionTitle = useCallback(async (sessionId, title) => {
    if (!isAuthenticated) return false;

    try {
      const response = await fetch(`${config.API_BASE_URL}/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ title })
      });

      if (response.ok) {
        const data = await response.json();
        setSessions(prev => prev.map(s => 
          s.id === sessionId ? data.session : s
        ));
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error updating session:', err);
      setError(err.message);
      return false;
    }
  }, [isAuthenticated, getAuthHeaders]);

  // Patch a single session in local state without a server round-trip.
  // Used e.g. when the backend pushes an auto-generated title over SSE.
  const applySessionUpdate = useCallback((sessionId, patch) => {
    if (!sessionId || !patch) return;
    setSessions(prev => prev.map(s => (s.id === sessionId ? { ...s, ...patch } : s)));
  }, []);

  // Select a session
  const selectSession = useCallback((sessionId) => {
    setCurrentSessionId(sessionId);
    if (sessionId) {
      sessionStorage.setItem('currentSessionId', sessionId);
    } else {
      sessionStorage.removeItem('currentSessionId');
    }
  }, []);

  const value = {
    sessions,
    currentSessionId,
    loading,
    error,
    fetchSessions,
    createSession,
    getSessionMessages,
    deleteSession,
    updateSessionTitle,
    applySessionUpdate,
    selectSession
  };

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
};

export default SessionContext;

